'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useLenis } from '@/providers/LenisProvider'
import {
  pushScrollDepthToDataLayer,
  SCROLL_DEPTH_THRESHOLDS,
  type ScrollDepthPercent,
} from '@/utilities/gtmScrollDepth'

function getScrollProgress(): number {
  const scrollTop = window.pageYOffset ?? document.documentElement.scrollTop
  const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
  return scrollHeight > 0 ? Math.min(Math.max(scrollTop / scrollHeight, 0), 1) : 0
}

export function GTMScrollDepth(): null {
  const pathname = usePathname()
  const firedRef = useRef<Set<ScrollDepthPercent>>(new Set())
  const pathnameRef = useRef(pathname)
  const { lenis } = useLenis()

  useEffect(() => {
    if (pathnameRef.current !== pathname) {
      pathnameRef.current = pathname
      firedRef.current = new Set()
    }

    const checkAndPush = (progress: number) => {
      const percent = Math.round(progress * 100)
      for (const threshold of SCROLL_DEPTH_THRESHOLDS) {
        if (percent >= threshold && !firedRef.current.has(threshold as ScrollDepthPercent)) {
          firedRef.current.add(threshold as ScrollDepthPercent)
          pushScrollDepthToDataLayer(threshold as ScrollDepthPercent, pathname)
        }
      }
    }

    const handleScroll = () => checkAndPush(getScrollProgress())

    if (lenis) {
      const onLenisScroll = (e: { progress: number }) => checkAndPush(e.progress)
      lenis.on('scroll', onLenisScroll)
      window.addEventListener('scroll', handleScroll, { passive: true })
      checkAndPush(getScrollProgress())

      return () => {
        lenis.off('scroll', onLenisScroll)
        window.removeEventListener('scroll', handleScroll)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    checkAndPush(getScrollProgress())

    return () => window.removeEventListener('scroll', handleScroll)
  }, [pathname, lenis])

  return null
}
